import React, { useState, useEffect } from 'react';
import SettingsService from '../../services/SettingsService';

const SystemSection = ({ settings, onChange }) => {
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await SettingsService.getHealthMetrics();
        if (!cancelled) {
          setHealth(data);
          setHealthLoading(false);
        }
      } catch {
        if (!cancelled) setHealthLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Live System Status */}
      <div className="settings-card">
        <div className="settings-card-header">
          <i className="bi bi-activity" />
          System Status
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <span className="settings-row-label">Database Connection</span>
            <span className="settings-row-help">Current database connection status</span>
          </div>
          <div className="settings-row-control">
            {healthLoading ? (
              <span className="settings-indicator settings-indicator--blue"><i className="bi bi-arrow-repeat spin" /> Checking...</span>
            ) : health?.database ? (
              <span className="settings-indicator settings-indicator--green">
                <span className="settings-indicator-dot" />
                Connected ({health.database.currentActive} active)
              </span>
            ) : (
              <span className="settings-indicator settings-indicator--red">
                <span className="settings-indicator-dot" />
                Disconnected
              </span>
            )}
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <span className="settings-row-label">Memory Usage</span>
            <span className="settings-row-help">Current server heap memory usage</span>
          </div>
          <div className="settings-row-control">
            {healthLoading ? (
              <span className="settings-indicator settings-indicator--blue">—</span>
            ) : (
              <span className={`settings-indicator settings-indicator--${
                health?.memory?.system?.percentage > 85 ? 'red' : 'green'
              }`}>
                {health?.memory?.process?.heapUsed ?? '?'} MB / {health?.memory?.process?.heapTotal ?? '?'} MB
              </span>
            )}
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <span className="settings-row-label">Server Uptime</span>
            <span className="settings-row-help">Time since last server restart</span>
          </div>
          <div className="settings-row-control">
            <span className="settings-indicator settings-indicator--blue">
              {healthLoading ? '—' : formatUptime(health?.server?.uptime)}
            </span>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <span className="settings-row-label">Health Score</span>
            <span className="settings-row-help">Overall server health rating</span>
          </div>
          <div className="settings-row-control">
            {healthLoading ? (
              <span className="settings-indicator settings-indicator--blue">—</span>
            ) : (
              <span className={`settings-indicator settings-indicator--${
                health?.health?.score >= 80 ? 'green' : health?.health?.score >= 60 ? 'blue' : 'red'
              }`}>
                {health?.health?.score ?? '?'}/100 — {health?.health?.status ?? 'unknown'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Configurable System Settings */}
      <div className="settings-card">
        <div className="settings-card-header">
          <i className="bi bi-sliders" />
          System Thresholds
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <span className="settings-row-label">Data Retention</span>
            <span className="settings-row-help">Auto-delete sync history older than N days (0 = keep forever)</span>
          </div>
          <div className="settings-row-control">
            <input
              type="number"
              className="settings-number-input"
              min={0}
              max={365}
              value={settings['system.dataRetentionDays'] ?? 90}
              onChange={e => onChange('system.dataRetentionDays', Math.max(0, Number(e.target.value)))}
            />
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <span className="settings-row-label">Memory Warning Threshold</span>
            <span className="settings-row-help">Alert when system memory usage exceeds this percentage</span>
          </div>
          <div className="settings-row-control">
            <input
              type="number"
              className="settings-number-input"
              min={50}
              max={99}
              value={settings['system.memoryWarningPercent'] ?? 85}
              onChange={e => onChange('system.memoryWarningPercent', Math.min(99, Math.max(50, Number(e.target.value))))}
            />
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <span className="settings-row-label">CPU Warning Threshold</span>
            <span className="settings-row-help">Alert when CPU usage exceeds this percentage</span>
          </div>
          <div className="settings-row-control">
            <input
              type="number"
              className="settings-number-input"
              min={50}
              max={99}
              value={settings['system.cpuWarningPercent'] ?? 90}
              onChange={e => onChange('system.cpuWarningPercent', Math.min(99, Math.max(50, Number(e.target.value))))}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

function formatUptime(seconds) {
  if (!seconds) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default SystemSection;
